@if (config.dispute; as dispute) {
  <!-- Dispute Header -->
  <div class="mb-6">
    <h1 class="text-xl md:text-2xl font-bold text-text-primary mb-2">
      Dispute: {{ dispute.projectMilestone?.name }}
    </h1>
    @if (isBusiness()) {
      <p class="text-sm text-text-secondary">
        Issued by {{ getIssuerName() }} • 
        {{ dispute.createdAt | date: "dd MMM yyyy 'at' h:mm a" }}
      </p>
    } @else {
      <p class="text-sm text-text-secondary">
        {{ dispute.createdAt | date: "dd MMM yyyy 'at' h:mm a" }}
      </p>
    }
  </div>

  <!-- Issue, Desired Outcome, Expectation, and Evidence Documents Section. -->
  <div class="p-6 mb-6 bg-white rounded-2xl main-shadow">
    <!-- Issue Section -->
    <h2 class="text-lg font-semibold mb-4">Issue:</h2>
    <ul class="list-disc pl-6 space-y-2 mb-6">
      @for (issue of parseIssues(dispute.describeIssue); track $index) {
        <li class="text-base">{{ issue }}</li>
      }
      @if (parseIssues(dispute.describeIssue).length === 0) {
        <li class="text-base">{{ dispute.describeIssue }}</li>
      }
    </ul>

    <!-- Desired Outcome Section -->
    <h2 class="text-lg font-semibold mb-4">Desired Outcome:</h2>
    <ul class="list-disc pl-6 space-y-2 mb-6">
      @if (getDesiredOutcomeLabel(dispute.desiredOutcome); as desiredOutcomeLabel) {
        <li class="text-base">{{ desiredOutcomeLabel }}</li>
      } @else {
        <li class="text-base text-text-secondary">—</li>
      }
    </ul>

    <!-- Expectation Section -->
    <h2 class="text-lg font-semibold mb-4">Expectation:</h2>
    <ul class="list-disc pl-6 space-y-2 mb-6">
      @for (expectation of parseExpectations(dispute.expectation); track $index) {
        <li class="text-base">{{ expectation }}</li>
      }
      @if (parseExpectations(dispute.expectation).length === 0) {
        <li class="text-base">{{ dispute.expectation }}</li>
      }
    </ul>

    <!-- Evidence Documents Section (if any) -->
    @if (getEvidenceDocuments(dispute.resolutionDocuments).length > 0) {
      <h2 class="text-lg font-semibold mb-4">Evidence Documents:</h2>
      <div class="space-y-3">
        @for (doc of getEvidenceDocuments(dispute.resolutionDocuments); track doc.id) {
          <div
            role="button"
            tabindex="0"
            class="bg-[#F9FAFB] p-4 rounded-lg flex justify-between items-center cursor-pointer hover:bg-[#F3F4F6] transition-colors"
            (click)="downloadFile.emit({ disputeId: dispute.id, document: doc })"
            (keydown.enter)="downloadFile.emit({ disputeId: dispute.id, document: doc })"
            (keydown.space)="downloadFile.emit({ disputeId: dispute.id, document: doc }); $event.preventDefault()"
          >
            <div class="flex-1 min-w-0">
              <div class="font-medium text-base truncate">{{ doc.fileName }}</div>
              <div class="text-sm text-text-secondary mt-1">
                Uploaded on {{ doc.createdAt | date: "dd MMM yyyy, HH:mm" }}
              </div>
            </div>
            <div class="flex items-center gap-2 ml-4 shrink-0">
              <img src="/icons/projects/download.svg" alt="Download" class="w-[1.125rem] h-[1.125rem] opacity-80" />
            </div>
          </div>
        }
      </div>
    }
  </div>

  <!-- Rejection reason (shown to both parties when a previous resolution was rejected) -->
  @if (dispute.reason) {
    <div class="p-6 mb-6 rounded-lg border-2 bg-red-50 border-red-200">
      <div class="flex gap-3 items-start">
        <i nz-icon nzType="close-circle" nzTheme="fill" class="text-red-600 text-xl flex-shrink-0 mt-0.5"></i>
        <div class="min-w-0 flex-1">
          <p class="text-base font-semibold text-red-900 mb-1">Rejection reason</p>
          <p class="text-base text-red-800 m-0">{{ dispute.reason }}</p>
          <p class="text-sm text-red-700 mt-2 m-0">You can propose a new mutual resolution or submit a settlement agreement below.</p>
        </div>
      </div>
    </div>
  }

  <!-- Submission Status Section -->
  @if (hasSubmitted() && getSubmissionStatusMessage()) {
    @if (isFormalResolutionType()) {
      <div class="p-6 mb-6 rounded-lg border-2" style="background-color: #fffbeb; border-color: #f59e0b;">
        <div class="flex gap-3 items-start">
          <i nz-icon nzType="clock-circle" nzTheme="fill" class="text-2xl flex-shrink-0 mt-0.5" style="color: #d97706;"></i>
          <div class="min-w-0 flex-1">
            <p class="text-base font-semibold mb-1" style="color: #92400e;">Your settlement agreement has been sent.</p>
            <p class="text-base mb-4" style="color: #b45309;">
              The other party has been notified to review it. You will be notified once they accept or reject the agreement.
            </p>
            @if (dispute.disputeType) {
              <p class="text-sm mb-4" style="color: #92400e;">
                <strong>Resolution Type:</strong> {{ getResolutionTypeLabel(dispute.disputeType) }}
              </p>
            }
            <button
              nz-popconfirm
              nzPopconfirmTitle="Are you sure you want to cancel your submission? This will remove your submitted resolution and any uploaded documents. You can resubmit after cancelling."
              (nzOnConfirm)="cancelSubmission.emit(dispute.id)"
              nz-button
              nzType="default"
              [disabled]="config.loading"
              [nzOkText]="'Yes, cancel'"
              [nzCancelText]="'No, keep it'"
              nzPopconfirmPlacement="top"
              nz-tooltip
              nzTooltipTitle="Retract the agreement to make changes and submit a new version."
              style="border-color: #d97706; color: #92400e;"
            >
              Retract Settlement Agreement
            </button>
          </div>
        </div>
      </div>
    } @else {
    <div class="p-6 mb-6 rounded-lg border-2 bg-blue-50 border-blue-200">
        <p class="text-base text-primary mb-2">
          {{ getSubmissionStatusMessage() }}
        </p>
        @if (dispute.disputeType) {
          <p class="text-sm text-text-secondary mb-4">
            <strong>Resolution Type:</strong> {{ getResolutionTypeLabel(dispute.disputeType) }}
          </p>
        }
        <button
          nz-popconfirm
          nzPopconfirmTitle="Are you sure you want to cancel your submission? This will remove your submitted resolution and any uploaded documents. You can resubmit after cancelling."
          (nzOnConfirm)="cancelSubmission.emit(dispute.id)"
          nz-button
          nzType="default"
          nzDanger
          [disabled]="config.loading"
          [nzOkText]="'Yes, cancel'"
          [nzCancelText]="'No, keep it'"
          nzPopconfirmPlacement="top"
        >
          Cancel Mutual Resolution
        </button>
    </div>
    }
  }

  <!-- Dispute Resolution Options Section -->
  <div class="p-6 mb-6 bg-white rounded-2xl border border-gray-200">
    <!-- Mutual Resolution Reached (Recommended) Section -->
    @if (!otherPartySelectedFormalResolution()) {
    <div [class.opacity-50]="dispute.disputeType === DisputeType.FORMAL_RESOLUTION || dispute.disputeType === DisputeType.FORMAL_RESOLUTION_WITH_PARTIAL_RELEASE || dispute.disputeType === DisputeType.FORMAL_RESOLUTION_WITH_REFUND">
      <h2 class="text-lg font-semibold mb-4">
        <span [class.text-gray-400]="dispute.disputeType === DisputeType.FORMAL_RESOLUTION || dispute.disputeType === DisputeType.FORMAL_RESOLUTION_WITH_PARTIAL_RELEASE || dispute.disputeType === DisputeType.FORMAL_RESOLUTION_WITH_REFUND" class="text-text-primary">Mutual Resolution Reached</span>
        <span class="text-blue-600 font-normal"> (Recommended)</span>
      </h2>
      @if (!(otherPartySelectedMutualResolution() && !hasSubmitted())) {
        <ul class="list-disc pl-6 space-y-2 mb-4 text-base" [class.text-gray-400]="dispute.disputeType === DisputeType.FORMAL_RESOLUTION || dispute.disputeType === DisputeType.FORMAL_RESOLUTION_WITH_PARTIAL_RELEASE || dispute.disputeType === DisputeType.FORMAL_RESOLUTION_WITH_REFUND" [class.text-text-primary]="dispute.disputeType !== DisputeType.FORMAL_RESOLUTION && dispute.disputeType !== DisputeType.FORMAL_RESOLUTION_WITH_PARTIAL_RELEASE && dispute.disputeType !== DisputeType.FORMAL_RESOLUTION_WITH_REFUND">
          <li>If this dispute was resolved through direct negotiation between both parties, and no formal judgment was made, you may select this option to propose closing the dispute.</li>
          <li>The other party will be asked to review and approve this resolution. Once approved, the dispute will be marked as resolved and the project will be reopened to continue as normal.</li>
        </ul>
      }
      @if (dispute.disputeType === DisputeType.INITIAL_ATTEMPT) {
        <div class="flex justify-center mb-6">
          <button
            nz-popconfirm
            nzPopconfirmTitle="Are you sure you want to propose a mutual resolution for this dispute?"
            (nzOnConfirm)="agreeToMutualResolution.emit({ disputeId: dispute.id, isAccepting: false })"
            nz-button
            nzType="default"
            class="hover:bg-blue-100 border-blue-600 text-blue-600"
            [nzOkText]="'Confirm'"
            [nzCancelText]="'Cancel'"
            [nzPopconfirmPlacement]="'top'"
            [disabled]="config.loading || (dispute.businessAgreedToResolve && dispute.clientAgreedToResolve)"
          >
            This dispute has been resolved
          </button>
        </div>
      }
      @if (dispute.disputeType === DisputeType.MUTUAL_RESOLUTION && hasSubmitted() && !otherPartyHasSubmitted()) {
        <div class="mb-6 p-4 rounded-lg bg-blue-50 border border-blue-200 flex gap-3 items-start w-full">
          <i nz-icon nzType="info-circle" nzTheme="fill" class="text-blue-600 text-xl flex-shrink-0 mt-0.5"></i>
          <div class="min-w-0">
            <p class="text-base font-semibold text-blue-900 mb-0.5">Your mutual resolution has been sent.</p>
            <p class="text-base text-blue-800 m-0">The other party has been notified to review it. You will be notified once they accept or reject the resolution.</p>
          </div>
        </div>
      }
      @if (otherPartySelectedMutualResolution() && !hasSubmitted()) {
        <div class="mb-6 mx-auto w-3/4 p-6 rounded-lg border-2" style="background-color: #fffbeb; border-color: #f59e0b;">
          <div class="flex gap-3 mb-4">
            <i nz-icon nzType="exclamation-circle" nzTheme="fill" class="text-2xl flex-shrink-0 mt-0.5" style="color: #d97706;"></i>
            <div class="text-center sm:text-left flex-1">
              <p class="text-base font-semibold mb-1" style="color: #92400e;">
                @if (config.userRole === 'client') {
                  The business has submitted a mutual resolution for your approval.
                } @else {
                  The client has submitted a mutual resolution for your approval.
                }
              </p>
              <p class="text-base mb-0" style="color: #b45309;">Once approved, the dispute will be closed and the project will resume as normal.</p>
            </div>
          </div>
          @if (rejectFormDisputeId !== dispute.id) {
            <div class="flex flex-col items-center gap-3">
              <button
                nz-popconfirm
                nzPopconfirmTitle="Are you sure you want to accept this mutual resolution?"
                (nzOnConfirm)="agreeToMutualResolution.emit({ disputeId: dispute.id, isAccepting: true })"
                nz-button
                [nzOkText]="'Confirm'"
                [nzCancelText]="'Cancel'"
                nzType="primary"
                [nzPopconfirmPlacement]="'top'"
                [disabled]="config.loading"
              >
                Accept Mutual Resolution
              </button>
              <button
                nz-button
                nzType="default"
                nzDanger
                [disabled]="config.loading"
                (click)="openRejectForm(dispute.id)"
              >
                Reject Mutual Resolution
              </button>
            </div>
          } @else {
            <div class="mt-4 p-4 rounded-lg border border-gray-200 bg-white">
              <p class="text-sm font-medium text-text-primary mb-2">Reason for rejection (required)</p>
              <textarea
                nz-input
                [(ngModel)]="rejectReason"
                [nzAutosize]="{ minRows: 3, maxRows: 6 }"
                placeholder="Please explain why you are rejecting this mutual resolution."
                class="w-full mb-3"
              ></textarea>
              <div class="flex gap-2 justify-end">
                <button nz-button nzType="default" (click)="cancelRejectForm()">Cancel</button>
                <button nz-button nzType="primary" nzDanger [disabled]="!rejectReason?.trim() || config.loading" (click)="submitReject()">
                  Submit rejection
                </button>
              </div>
            </div>
          }
        </div>
      }
    </div>
    }
    @if (!otherPartySelectedMutualResolution() && !otherPartySelectedFormalResolution()) {
      <div class="flex items-center my-6">
        <div class="flex-1 border-t border-gray-300"></div>
        <span class="px-4 text-sm text-text-secondary">or</span>
        <div class="flex-1 border-t border-gray-300"></div>
      </div>
    }
    <!-- Formal Resolution (Third-Party Decision) Section -->
    @if (!otherPartySelectedMutualResolution()) {
    <div [class.opacity-50]="dispute.disputeType === DisputeType.MUTUAL_RESOLUTION">
      <h2 class="text-lg font-semibold mb-4" [class.text-gray-400]="dispute.disputeType === DisputeType.MUTUAL_RESOLUTION" [class.text-text-primary]="dispute.disputeType !== DisputeType.MUTUAL_RESOLUTION">Formal Resolution (Third-Party Decision)</h2>
      <p class="text-base mb-4" [class.text-gray-400]="dispute.disputeType === DisputeType.MUTUAL_RESOLUTION" [class.text-text-primary]="dispute.disputeType !== DisputeType.MUTUAL_RESOLUTION">
        If this dispute was resolved through a <strong>binding outcome</strong> — such as arbitration, litigation, or a signed settlement agreement reached through mediation — please upload the relevant document here.
      </p>
      <p class="text-base mb-2" [class.text-gray-400]="dispute.disputeType === DisputeType.MUTUAL_RESOLUTION" [class.text-text-primary]="dispute.disputeType !== DisputeType.MUTUAL_RESOLUTION">This is typically required when:</p>
      <ul class="list-disc pl-6 space-y-1 mb-4 text-base" [class.text-gray-400]="dispute.disputeType === DisputeType.MUTUAL_RESOLUTION" [class.text-text-primary]="dispute.disputeType !== DisputeType.MUTUAL_RESOLUTION">
        <li>Both parties were unable to reach a mutual resolution through direct negotiation, and</li>
        <li>A formal decision or signed agreement has been issued to conclude the dispute.</li>
      </ul>
      <p class="text-base mb-4" [class.text-gray-400]="dispute.disputeType === DisputeType.MUTUAL_RESOLUTION" [class.text-text-primary]="dispute.disputeType !== DisputeType.MUTUAL_RESOLUTION">
        Once the document is uploaded and accepted by the other party, the dispute will be marked as resolved and the project will be reopened.
      </p>
      <p class="text-base mb-4" [class.text-gray-400]="dispute.disputeType === DisputeType.MUTUAL_RESOLUTION" [class.text-text-primary]="dispute.disputeType !== DisputeType.MUTUAL_RESOLUTION">Only PDF files are accepted.</p>

      <!-- File Upload Area - Hide if other party selected formal resolution -->
      @if (!otherPartySelectedFormalResolution()) {
        <nz-upload
          nzType="drag"
          [nzMultiple]="false"
          [nzAccept]="'application/pdf'"
          [nzBeforeUpload]="beforeUpload"
          [nzShowUploadList]="false"
          class="w-full"
          [nzDisabled]="config.uploading || hasSubmitted() || otherPartyHasSubmitted() || dispute.disputeType === DisputeType.MUTUAL_RESOLUTION"
        >
          <p class="ant-upload-drag-icon">
            <i nz-icon nzType="upload" nzTheme="outline" class="text-4xl text-gray-400"></i>
          </p>
          <p class="ant-upload-text">Drag or upload a PDF file</p>
          <p class="ant-upload-hint">Only PDF files are accepted.</p>
        </nz-upload>
      }

      <!-- Uploaded Documents List (Resolution Documents Only) - hidden from non-creator when INITIAL_ATTEMPT -->
      @if (showResolutionDocumentsSection() && getResolutionDocuments(dispute.resolutionDocuments).length > 0) {
        <div class="mt-4 space-y-3">
          @for (doc of getResolutionDocuments(dispute.resolutionDocuments); track doc.id) {
            <div
              role="button"
              tabindex="0"
              class="bg-[#F9FAFB] p-4 rounded-lg flex justify-between items-center cursor-pointer hover:bg-[#F3F4F6] transition-colors"
              (click)="downloadFile.emit({ disputeId: dispute.id, document: doc })"
              (keydown.enter)="downloadFile.emit({ disputeId: dispute.id, document: doc })"
              (keydown.space)="downloadFile.emit({ disputeId: dispute.id, document: doc }); $event.preventDefault()"
            >
              <div class="flex-1 min-w-0">
                <div class="font-medium text-base truncate">{{ doc.fileName }}</div>
                <div class="text-sm text-text-secondary mt-1">
                  Uploaded on {{ doc.createdAt | date: "dd MMM yyyy, HH:mm" }}
                </div>
              </div>
              <div class="flex items-center gap-2 ml-4 shrink-0">
                <img src="/icons/projects/download.svg" alt="Download" class="w-[1.125rem] h-[1.125rem] opacity-80" />
                @if (!hasSubmitted() && !otherPartyHasSubmitted()) {
                  <button
                    nz-button
                    nzType="text"
                    nzSize="small"
                    nzDanger
                    type="button"
                    class="flex items-center"
                    (click)="$event.stopPropagation(); deleteFile.emit({ disputeId: dispute.id, documentId: doc.id })"
                  >
                    <i nz-icon nzType="delete" nzTheme="outline" class="text-lg"></i>
                  </button>
                }
              </div>
            </div>
          }
        </div>
      }

      <!-- Partial Release or Refund Section - Hide if other party selected mutual resolution -->
      @if (!otherPartySelectedMutualResolution()) {
        <div class="mt-8 pt-8 border-t border-gray-200">
          <h2 class="text-lg font-semibold mb-4" [class.text-gray-400]="dispute.disputeType === DisputeType.MUTUAL_RESOLUTION" [class.text-text-primary]="dispute.disputeType !== DisputeType.MUTUAL_RESOLUTION">Partial Release or Refund</h2>
          <p class="text-base mb-4" [class.text-gray-400]="dispute.disputeType === DisputeType.MUTUAL_RESOLUTION" [class.text-text-secondary]="dispute.disputeType !== DisputeType.MUTUAL_RESOLUTION">Please indicate if there is a Partial Release or Refund:</p>
          <nz-radio-group 
            [ngModel]="getPartialReleaseOrRefundValue()" 
            (ngModelChange)="!otherPartySelectedFormalResolution() && !isFormalResolutionChoiceLocked() && onPartialReleaseOrRefundChange($event)"
            [nzDisabled]="config.loading || dispute.disputeType === DisputeType.MUTUAL_RESOLUTION || otherPartySelectedFormalResolution() || isFormalResolutionChoiceLocked()" 
            class="flex gap-6"
          >
            <label nz-radio nzValue="partial_release">Partial Release</label>
            <label nz-radio nzValue="refund">Refund</label>
            <label nz-radio nzValue="none">None</label>
          </nz-radio-group>

          <!-- Info message when user has sent settlement agreement -->
          @if (isFormalResolutionChoiceLocked() && !otherPartyHasSubmitted()) {
            <div class="mt-6 p-4 rounded-lg bg-blue-50 border border-blue-200 flex gap-3 items-start w-full">
              <i nz-icon nzType="info-circle" nzTheme="fill" class="text-blue-600 text-xl flex-shrink-0 mt-0.5"></i>
              <div class="min-w-0">
                <p class="text-base font-semibold text-blue-900 mb-0.5">Your settlement agreement has been sent.</p>
                <p class="text-base text-blue-800 m-0">The other party has been notified to review it. You will be notified once they accept or reject the agreement.</p>
              </div>
            </div>
          }
        </div>
      }

      <!-- Submit Settlement Agreement Button - Only show if user hasn't submitted, other party hasn't selected mutual resolution, and other party hasn't selected formal resolution -->
      @if (!hasSubmitted() && !otherPartySelectedMutualResolution() && !otherPartySelectedFormalResolution()) {
        @if (otherPartyHasSubmitted()) {
          <div class="flex justify-center mt-6">
            <button
              nz-popconfirm
              nzPopconfirmTitle="Are you sure you want to accept the terms of the settlement agreement?"
              (nzOnConfirm)="emitAcceptSettlementAgreement()"
              nz-button
              [nzOkText]="'Confirm'"
              [nzCancelText]="'Cancel'"
              nzType="primary"
              [nzPopconfirmPlacement]="'top'"
              [disabled]="getResolutionDocuments(dispute.resolutionDocuments).length === 0 || config.loading || dispute.disputeType === DisputeType.MUTUAL_RESOLUTION"
            >
              Accept the terms of the settlement agreement
            </button>
          </div>
        } @else {
          <div class="flex justify-center mt-6">
            <button
              nz-popconfirm
              nzPopconfirmTitle="Are you sure you want to submit a formal resolution?"
              (nzOnConfirm)="submitSettlementAgreement.emit(dispute.id)"
              nz-button
              nzType="primary"
              [nzOkText]="'Confirm'"
              [nzCancelText]="'Cancel'"
              [nzPopconfirmPlacement]="'top'"
              [disabled]="getResolutionDocuments(dispute.resolutionDocuments).length === 0 || config.loading || dispute.disputeType === DisputeType.MUTUAL_RESOLUTION"
            >
              Submit Settlement Agreement
            </button>
          </div>
        }
      }
    </div>
    <!-- Accept button below Formal Resolution section when other party selected it -->
    @if (otherPartySelectedFormalResolution() && !hasSubmitted()) {
      <div class="mt-6 mx-auto w-3/4 p-6 rounded-lg border-2" style="background-color: #fffbeb; border-color: #f59e0b;">
        <div class="flex gap-3 mb-4">
          <i nz-icon nzType="exclamation-circle" nzTheme="fill" class="text-2xl flex-shrink-0 mt-0.5" style="color: #d97706;"></i>
          <div class="text-center sm:text-left flex-1">
            <p class="text-base font-semibold mb-1" style="color: #92400e;">Your {{ config.userRole === 'client' ? 'Business' : 'Client' }} has submitted a settlement agreement for your review.</p>
            <p class="text-base mb-0" style="color: #b45309;">Accepting this settlement agreement will close the dispute and reopen the project. Any refunds or partial refunds will be handled by the Handshake team.</p>
          </div>
        </div>
        @if (rejectFormDisputeId !== dispute.id) {
          <div class="flex flex-col items-center gap-3">
            <button
              nz-popconfirm
              nzPopconfirmTitle="Are you sure you want to accept the terms of the settlement agreement?"
              (nzOnConfirm)="emitAcceptSettlementAgreement()"
              nz-button
              [nzOkText]="'Confirm'"
              [nzCancelText]="'Cancel'"
              nzType="primary"
              [nzPopconfirmPlacement]="'top'"
              [disabled]="getResolutionDocuments(dispute.resolutionDocuments).length === 0 || config.loading"
            >
              Accept the terms of the settlement agreement
            </button>
            <button
              nz-button
              nzType="default"
              nzDanger
              [disabled]="config.loading"
              (click)="openRejectForm(dispute.id)"
            >
              Reject Settlement Agreement
            </button>
          </div>
        } @else {
          <div class="mt-4 p-4 rounded-lg border border-gray-200 bg-white">
            <p class="text-sm font-medium text-text-primary mb-2">Reason for rejection (required)</p>
            <textarea
              nz-input
              [(ngModel)]="rejectReason"
              [nzAutosize]="{ minRows: 3, maxRows: 6 }"
              placeholder="Please explain why you are rejecting this settlement agreement."
              class="w-full mb-3"
            ></textarea>
            <div class="flex gap-2 justify-end">
              <button nz-button nzType="default" (click)="cancelRejectForm()">Cancel</button>
              <button nz-button nzType="primary" nzDanger [disabled]="!rejectReason?.trim() || config.loading" (click)="submitReject()">
                Submit rejection
              </button>
            </div>
          </div>
        }
      </div>
    }
    }
  </div>

  <!-- Feeling Stuck? Section -->
  <div class="p-6 mb-6 bg-purple-50 rounded-2xl border border-purple-100">
    <div class="flex flex-col gap-4">
      <div class="flex items-center gap-4">
        <div class="flex-shrink-0 w-10 h-10 bg-purple-600 rounded-full flex items-center justify-center">
          <i nz-icon nzType="question-circle" nzTheme="outline" class="text-white text-xl"></i>
        </div>
        <div class="min-w-0">
          <h2 class="text-lg font-semibold mb-1">Feeling Stuck?</h2>
          <p class="text-base text-text-secondary">
            Contact us on Whatsapp or send us an email anytime for assistance.
          </p>
        </div>
      </div>
      <div class="flex flex-col sm:flex-row gap-3">
        <button nz-button nzType="default" class="border-purple-200 text-purple-600 hover:bg-purple-100 w-full sm:w-auto" (click)="openWhatsApp.emit()">
          <i nz-icon nzType="phone" nzTheme="outline" class="mr-2"></i>
          Send a message on Whatsapp
        </button>
        <button nz-button nzType="default" class="border-purple-200 text-purple-600 hover:bg-purple-100 w-full sm:w-auto" (click)="contactSupport.emit()">
          <i nz-icon nzType="mail" nzTheme="outline" class="mr-2"></i>
          Contact Handshake Support
        </button>
      </div>
    </div>
  </div>

  <!-- Dispute Resolution Guide Section -->
  <div class="p-6 mb-6 bg-white rounded-2xl main-shadow">
    <h2 class="text-lg font-semibold mb-4">Dispute Resolution Guide</h2>
    <app-core-dispute-resolution-guide />
  </div>
}
